import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Users, ArrowRight, Sparkles, Heart, Shield, 
  Calendar, MapPin, Linkedin, Clock, Star, 
  MessageCircle, X, Send, ChevronRight, Coffee
} from "lucide-react";
import { SukoonFloatingWidget, SukoonSection } from "../components/SukoonWidget";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// HeySolo.ai Logo - Graphic Text Style
const HeySoloLogo = ({ className = "", dark = false }) => (
  <div className={`${className}`}>
    <span className={`text-xl md:text-2xl tracking-[0.25em] font-light ${dark ? 'text-luxe-black' : 'text-cream'}`} style={{ fontFamily: "'Playfair Display', serif" }}>
      HEYSOLO<span className="text-teal-400">.</span>AI
    </span>
  </div>
);

// Host Tier Badge Component
const HostTierBadge = ({ tier }) => {
  const tierConfig = {
    expert: { label: "The Expert", className: "tier-expert", years: "15+ years" },
    rising: { label: "The Rising Pro", className: "tier-rising", years: "5-15 years" },
    catalyst: { label: "The Catalyst", className: "tier-catalyst", years: "0-5 years" }
  };
  const config = tierConfig[tier] || tierConfig.catalyst;
  
  return (
    <span className={`${config.className} text-xs px-3 py-1.5 rounded-full font-semibold`}>
      {config.label}
    </span>
  );
};

// Host Card Component
const HostCard = ({ host }) => (
  <div className="card-warm p-6" data-testid={`host-card-${host.id}`}>
    <div className="flex items-start gap-4">
      <div className="img-luxe w-16 h-16 rounded-xl overflow-hidden flex-shrink-0">
        <img 
          src={host.image} 
          alt={host.name}
          className="w-full h-full object-cover"
        />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <h4 className="font-semibold text-luxe-black truncate" style={{ fontFamily: "'Playfair Display', serif" }}>{host.name}</h4>
          <a href={host.linkedin} target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:text-teal-700 flex-shrink-0">
            <Linkedin className="w-4 h-4" />
          </a>
        </div>
        <p className="text-sm text-luxe-black/60 mb-2 truncate">{host.title}</p>
        <HostTierBadge tier={host.tier} />
      </div>
    </div>
    <p className="text-sm text-luxe-black/70 mt-4 line-clamp-2">{host.bio}</p>
    <div className="flex items-center gap-4 mt-4 text-sm text-luxe-black/50">
      <span className="flex items-center gap-1">
        <Star className="w-4 h-4 text-champagne fill-champagne" />
        {host.rating}
      </span>
      <span>{host.tables_hosted} tables hosted</span>
    </div>
  </div>
);

// Event Card Component  
const EventCard = ({ event, onBook }) => (
  <div className="card-luxe overflow-hidden group" data-testid={`event-card-${event.event_id}`}>
    <div className="relative h-48 overflow-hidden">
      <img 
        src={event.image_url} 
        alt={event.title}
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-luxe-black/60 to-transparent"></div>
      <div className="absolute top-3 left-3">
        <span className={`px-3 py-1.5 rounded-full text-xs font-semibold ${
          event.segment === 'women' ? 'bg-teal-500 text-white' : 'bg-champagne text-luxe-black'
        }`}>
          {event.segment === 'women' ? 'Women Only' : 'Men Only'}
        </span>
      </div>
      <div className="absolute top-3 right-3 bg-luxe-black/80 backdrop-blur-sm px-3 py-1.5 rounded-full">
        <span className="text-cream font-semibold text-sm">{event.seats_left}/6 seats</span>
      </div>
    </div>
    <div className="p-5">
      <h3 className="font-semibold text-cream text-lg mb-3 group-hover:text-teal-400 transition-colors" style={{ fontFamily: "'Playfair Display', serif" }}>
        {event.title}
      </h3>
      <div className="space-y-2 text-sm text-cream/50 mb-4">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-teal-400" />
          {new Date(event.date).toLocaleDateString('en-IN', { weekday: 'short', month: 'short', day: 'numeric' })}
        </div>
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-teal-400" />
          {new Date(event.date).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
        </div>
        <div className="flex items-center gap-2">
          <MapPin className="w-4 h-4 text-teal-400" />
          {event.location}
        </div>
      </div>
      <div className="flex items-center justify-between pt-4 border-t border-cream/10">
        <span className="text-2xl font-bold text-cream">₹{event.price}</span>
        <Button 
          onClick={() => onBook(event)}
          className="btn-teal px-6 py-2"
          data-testid={`book-seat-${event.event_id}`}
        >
          Book a Seat
        </Button>
      </div>
    </div>
  </div>
);

const LandingPage = () => {
  const navigate = useNavigate();
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sukoonOpen, setSukoonOpen] = useState(false);
  const [events, setEvents] = useState([]);
  const [activeSegment, setActiveSegment] = useState('women');

  // Sample hosts data
  const hosts = [
    {
      id: 1,
      name: "Priya Sharma",
      title: "VP Engineering, Google",
      tier: "expert",
      image: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=200",
      linkedin: "https://linkedin.com",
      bio: "20+ years in tech leadership. Passionate about building inclusive teams and mentoring the next generation.",
      rating: 4.9,
      tables_hosted: 24
    },
    {
      id: 2,
      name: "Rahul Kapoor",
      title: "Founder, TechStart",
      tier: "rising",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200",
      linkedin: "https://linkedin.com",
      bio: "Serial entrepreneur with 2 successful exits. Love connecting founders and sharing startup war stories.",
      rating: 4.8,
      tables_hosted: 12
    },
    {
      id: 3,
      name: "Ananya Reddy",
      title: "Product Manager, Microsoft",
      tier: "catalyst",
      image: "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=200",
      linkedin: "https://linkedin.com",
      bio: "3 years into PM journey. Building a community for early-career professionals navigating corporate life.",
      rating: 4.7,
      tables_hosted: 6
    }
  ];

  // Sample events
  const sampleEvents = [
    {
      event_id: "evt_001",
      title: "Tech Leaders Dinner",
      segment: "women",
      date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
      location: "The Leela Palace, Bangalore",
      price: 2999,
      seats_left: 3,
      image_url: "https://images.unsplash.com/photo-1640006142200-97192741d184?w=600&q=80"
    },
    {
      event_id: "evt_002", 
      title: "Founders Roundtable",
      segment: "men",
      date: new Date(Date.now() + 10 * 24 * 60 * 60 * 1000).toISOString(),
      location: "ITC Gardenia, Bangalore",
      price: 3499,
      seats_left: 2,
      image_url: "https://images.unsplash.com/photo-1671053803599-68155dd2c7b6?w=600&q=80"
    },
    {
      event_id: "evt_003",
      title: "Creative Minds Brunch",
      segment: "women", 
      date: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(),
      location: "Toast & Tonic, Mumbai",
      price: 1999,
      seats_left: 4,
      image_url: "https://images.unsplash.com/photo-1733341127459-2a6f5c1a00dc?w=600&q=80"
    }
  ];

  useEffect(() => {
    setEvents(sampleEvents);
  }, []);

  const handleStartVibeMatch = async () => {
    if (!name.trim() || !email.trim() || !email.includes('@')) {
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await fetch(`${API}/vibe-match/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email })
      });
      const data = await response.json();
      if (data.success) {
        localStorage.setItem('heysolo_lead', JSON.stringify({ name, email, lead_id: data.lead_id }));
        navigate('/vibe-match', { state: { name, email, lead_id: data.lead_id } });
      }
    } catch (error) {
      localStorage.setItem('heysolo_lead', JSON.stringify({ name, email, lead_id: 'local_' + Date.now() }));
      navigate('/vibe-match', { state: { name, email } });
    }
    setIsLoading(false);
  };

  const handleBookSeat = (event) => {
    const lead = localStorage.getItem('heysolo_lead');
    if (!lead) {
      setShowLeadForm(true);
      return;
    }
    alert('Payment flow coming soon! This will integrate with Razorpay.');
  };

  const filteredEvents = events.filter(e => e.segment === activeSegment);

  return (
    <div className="min-h-screen bg-luxe-black">
      {/* Navigation - Transparent over video */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-transparent">
        <div className="max-w-7xl mx-auto px-6 py-6 flex items-center justify-between">
          <HeySoloLogo />
          
          <div className="hidden md:flex items-center gap-10">
            <a href="#sukoon" className="text-white/60 hover:text-white text-sm font-medium tracking-wide transition-colors flex items-center gap-2">
              <Heart className="w-4 h-4 text-teal-400" />
              Meet Sukoon
            </a>
            <a href="#how-it-works" className="text-white/60 hover:text-white text-sm font-medium tracking-wide transition-colors">How It Works</a>
            <a href="#marketplace" className="text-white/60 hover:text-white text-sm font-medium tracking-wide transition-colors">Tables</a>
            <a href="#hosts" className="text-white/60 hover:text-white text-sm font-medium tracking-wide transition-colors">Become a Host</a>
          </div>
          
          <button 
            onClick={() => setShowLeadForm(true)}
            className="btn-cinematic"
            data-testid="nav-cta"
          >
            Enter
          </button>
        </div>
      </nav>

      {/* Cinematic Hero Section */}
      <section className="hero-cinematic">
        {/* Video Background */}
        <div className="hero-video-container">
          <video 
            autoPlay 
            muted 
            loop 
            playsInline
            className="hero-video"
            poster="https://images.unsplash.com/photo-1671053803599-68155dd2c7b6?w=1920&q=80"
          >
            {/* Friends meeting at cafe - warm, friendly networking vibe */}
            <source src="https://videos.pexels.com/video-files/5529079/5529079-hd_1920_1080_30fps.mp4" type="video/mp4" />
          </video>
          {/* Vignette Overlay */}
          <div className="hero-vignette"></div>
          {/* Film Grain */}
          <div className="hero-grain"></div>
        </div>

        {/* Floating Content - Art Gallery Style */}
        <div className="hero-content-cinematic">
          {/* Philosophical Headline - Gallery Quote Style */}
          <h1 className="headline-philosophical">
            {/* The Hook - Elegant and Professional */}
            <span className="block intro-hook">
              If we remove your name, your title,<br/>
              your bank balance and all your social tags...
            </span>
            {/* The Soul Question */}
            <span className="block headline-soul mt-8">
              What are you inside?
            </span>
          </h1>

          {/* Bhagavad Gita Wisdom Block */}
          <div className="wisdom-block mt-10 max-w-2xl mx-auto">
            <div className="wisdom-divider"></div>
            <p className="wisdom-text">
              Inspired by the wisdom of the <em>Bhagavad Gita</em>—recognizing that our Soul is an identity beyond the body. 
              We invite you to shed the professional mask and discover your true self with like-minded seekers.
            </p>
            <div className="wisdom-divider"></div>
          </div>
          
          <div className="cta-cinematic mt-12">
            <button 
              onClick={() => navigate('/vibe-match')}
              className="btn-cinematic-soul"
              data-testid="hero-cta"
            >
              Begin the Self-Discovery
            </button>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="scroll-indicator">
          <span></span>
        </div>
      </section>

      {/* Credence Group Badge - Minimal */}
      <div className="bg-luxe-black py-8 border-t border-cream/5">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-center gap-3 text-xs text-cream/30 tracking-widest uppercase">
          <span>A</span>
          <span className="font-medium text-cream/40">Credence Group</span>
          <span>Initiative</span>
        </div>
      </div>

      {/* Sukoon AI Section */}
      <SukoonSection onOpenWidget={() => setSukoonOpen(true)} />

      {/* How It Works - Warm Section */}
      <section id="how-it-works" className="section-warm py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-teal-50 text-teal-700 rounded-full text-sm font-semibold mb-4">
              How It Works
            </span>
            <h2 className="text-4xl md:text-5xl text-luxe-black mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              From Match to Meetup
            </h2>
            <p className="text-luxe-black/60 text-lg max-w-2xl mx-auto">
              Three simple steps to find your professional tribe
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: <Sparkles className="w-7 h-7" />,
                title: "Take the Vibe-Match",
                description: "Answer 10 questions about your professional personality. Our AI understands what makes you tick."
              },
              {
                icon: <Heart className="w-7 h-7" />,
                title: "Discover Your Tribe",
                description: "Get matched with your Tribe Type - Tech Maverick, Zen Leader, Social Catalyst, or Grounded Leader."
              },
              {
                icon: <Users className="w-7 h-7" />,
                title: "Book Your Table",
                description: "Join a curated dinner of 6. Real conversations, real connections, zero awkwardness."
              }
            ].map((step, idx) => (
              <div key={idx} className="card-warm p-8 text-center">
                <div className="w-16 h-16 mx-auto bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl flex items-center justify-center text-white mb-6 shadow-lg shadow-teal-500/20">
                  {step.icon}
                </div>
                <h3 className="text-xl font-semibold text-luxe-black mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>{step.title}</h3>
                <p className="text-luxe-black/60">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Marketplace */}
      <section id="marketplace" className="bg-luxe py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 badge-luxe rounded-full text-sm font-semibold mb-4">
              Marketplace
            </span>
            <h2 className="text-4xl md:text-5xl text-cream mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              Upcoming Tables of 6
            </h2>
            <p className="text-cream/50 max-w-2xl mx-auto text-lg">
              Pre-paid seats ensure committed professionals. No-shows don't happen here.
            </p>
          </div>

          {/* Segment Toggle */}
          <div className="flex justify-center mb-12">
            <div className="segment-toggle inline-flex">
              <button
                onClick={() => setActiveSegment('women')}
                className={`segment-btn ${activeSegment === 'women' ? 'active-women' : ''}`}
                data-testid="segment-women"
              >
                Women-to-Women
              </button>
              <button
                onClick={() => setActiveSegment('men')}
                className={`segment-btn ${activeSegment === 'men' ? 'active-men' : ''}`}
                data-testid="segment-men"
              >
                Men-to-Men
              </button>
            </div>
          </div>

          {/* Events Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredEvents.map((event) => (
              <EventCard key={event.event_id} event={event} onBook={handleBookSeat} />
            ))}
          </div>

          {filteredEvents.length === 0 && (
            <div className="text-center py-16">
              <p className="text-cream/40 text-lg">No upcoming events in this segment. Check back soon!</p>
            </div>
          )}
        </div>
      </section>

      {/* Host Ecosystem - Warm Section */}
      <section id="hosts" className="section-warm py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div>
              <span className="inline-block px-4 py-2 bg-teal-50 text-teal-700 rounded-full text-sm font-semibold mb-4">
                Become a Host
              </span>
              <h2 className="text-4xl md:text-5xl text-luxe-black mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                Lead Conversations.<br/>Earn Honorariums.
              </h2>
              <p className="text-luxe-black/60 text-lg mb-8">
                As a verified host, you curate experiences for your professional community. 
                We handle logistics, you bring the expertise and warmth.
              </p>

              {/* Host Tiers */}
              <div className="space-y-4 mb-8">
                {[
                  { tier: 'expert', title: 'The Expert', years: '15+ years', desc: 'Industry veterans who set the tone' },
                  { tier: 'rising', title: 'The Rising Pro', years: '5-15 years', desc: 'Mid-career pros with fresh perspectives' },
                  { tier: 'catalyst', title: 'The Catalyst', years: '0-5 years', desc: 'Early-career connectors building community' }
                ].map((item) => (
                  <div key={item.tier} className="flex items-center gap-4 p-4 bg-white rounded-xl border border-luxe-black/10">
                    <HostTierBadge tier={item.tier} />
                    <div>
                      <p className="font-medium text-luxe-black">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <Button className="btn-champagne px-8 py-6 rounded-full text-lg" data-testid="apply-host">
                Apply to Host
                <ChevronRight className="ml-2 w-5 h-5" />
              </Button>
            </div>

            {/* Featured Hosts */}
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-luxe-black mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>Featured Hosts</h3>
              {hosts.map((host) => (
                <HostCard key={host.id} host={host} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 bg-gradient-to-br from-teal-600 via-teal-700 to-teal-800">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl text-white mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
            Your Tribe is Waiting
          </h2>
          <p className="text-teal-100/80 text-lg mb-10 max-w-2xl mx-auto">
            Take the 2-minute Vibe-Match and discover which professional tribe you belong to. 
            No sign-up required to start.
          </p>
          <Button 
            onClick={() => setShowLeadForm(true)}
            className="bg-white text-teal-700 hover:bg-cream text-lg px-10 py-6 rounded-full shadow-2xl hover:shadow-3xl transition-all font-semibold"
            data-testid="footer-cta"
          >
            Start the Vibe-Match
            <Sparkles className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-luxe-black">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <HeySoloLogo />
            <div className="flex items-center gap-6 text-sm text-cream/30">
              <a href="#" className="hover:text-cream transition-colors">Privacy</a>
              <a href="#" className="hover:text-cream transition-colors">Terms</a>
              <a href="#" className="hover:text-cream transition-colors">Contact</a>
              <span>© 2026 HeySolo.ai • A Credence Group Initiative</span>
            </div>
          </div>
        </div>
      </footer>

      {/* Lead Capture Modal */}
      {showLeadForm && (
        <div className="fixed inset-0 bg-luxe-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="lead-modal">
          <div className="bg-luxe-charcoal rounded-3xl p-8 max-w-md w-full shadow-2xl border border-cream/10">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-teal-500/25">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl text-cream" style={{ fontFamily: "'Playfair Display', serif" }}>Start Your Vibe-Match</h3>
              <p className="text-cream/50 mt-2">10 questions. 2 minutes. Discover your tribe.</p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-cream/60 mb-2 block">Your Name</label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter your name"
                  className="bg-luxe-graphite border-cream/15 text-cream placeholder:text-cream/30 focus:ring-teal-500 focus:border-teal-500"
                  data-testid="lead-name"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-cream/60 mb-2 block">Work Email</label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="bg-luxe-graphite border-cream/15 text-cream placeholder:text-cream/30 focus:ring-teal-500 focus:border-teal-500"
                  data-testid="lead-email"
                />
              </div>
            </div>

            <div className="mt-8 space-y-3">
              <Button 
                onClick={handleStartVibeMatch}
                disabled={isLoading || !name.trim() || !email.includes('@')}
                className="w-full btn-teal py-6 rounded-full text-lg"
                data-testid="lead-submit"
              >
                {isLoading ? 'Starting...' : 'Begin Vibe-Match'}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <button 
                onClick={() => setShowLeadForm(false)}
                className="w-full text-cream/40 hover:text-cream text-sm py-2 transition-colors"
              >
                Maybe later
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sukoon Floating Widget */}
      <SukoonFloatingWidget isOpen={sukoonOpen} setIsOpen={setSukoonOpen} />
    </div>
  );
};

export default LandingPage;
